import { useSetAtom } from "jotai";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  getStoredAuthFlow,
  getStoredAuthProvider,
  setStoredAuthCancelledError,
} from "../utils/storage";
import { AuthFlow, OnboardingSteps, ROUTES } from "../utils/constants";
import { userAtom } from "../jotai/atoms/user";
import useOnboardingSteps from "./useOnboardingSteps";
import useAnalytics from "./useAnalytics";
import { SIGNUP_METHODS, SignupOptionProperty } from "../utils/analytics";
import { handleVerifyAuth } from "../api-clients/usersAuthClient";
import Logger from "../utils/logger";
import useSaveAttributionMutation from "../react-query/mutations/use-save-attribution";
import useSavePhoenixClickMutation from "../react-query/mutations/use-save-phoenix-click";
import { getAttributionData } from "../utils/attribution";
import { getPhoenixClickId } from "../utils/phoenix-click";

const FALLBACK_PATH = ROUTES.ROOT.SIGN_UP;

export default function useVerifyAuth() {
  const [queryParams] = useSearchParams();
  const navigate = useNavigate();
  const setUser = useSetAtom(userAtom);
  const [token, setToken] = useState<string>();
  const base64Data = queryParams.get("data");
  const base64Error = queryParams.get("error");
  const [verificationInProgress, setVerificationInProgress] = useState<boolean>(
    () => !!base64Data || !!base64Error,
  );
  const authFlow = getStoredAuthFlow();
  const authFlowText =
    authFlow === AuthFlow.LOGIN ? "Logging in..." : "Creating account...";
  const { goToStep } = useOnboardingSteps();
  const {
    sendSuccessfulSignupEvent,
    sendFailedSignupEvent,
    sendSuccessfulSigninEvent,
  } = useAnalytics();
  const { mutate: saveAttribution } = useSaveAttributionMutation();
  const { mutate: savePhoenixClick } = useSavePhoenixClickMutation();

  // Handle error from OAuth redirect
  useEffect(() => {
    if (!base64Error || !verificationInProgress) return;

    setVerificationInProgress(false);
    const authProvider = getStoredAuthProvider();
    if (authProvider) {
      sendFailedSignupEvent({
        signup_method: SIGNUP_METHODS[authProvider],
        signup_option:
          authFlow === AuthFlow.LOGIN
            ? SignupOptionProperty.LOG_IN
            : SignupOptionProperty.SIGN_UP,
      });
    }

    try {
      setStoredAuthCancelledError(JSON.parse(atob(base64Error)));
    } catch (error) {
      Logger.error(`Failed to parse auth error from URL parameter: ${error}`);
    }
    navigate(FALLBACK_PATH);
  }, [
    authFlow,
    base64Error,
    navigate,
    sendFailedSignupEvent,
    verificationInProgress,
  ]);

  // Extract token from base64 data
  useEffect(() => {
    if (!base64Data) return;

    try {
      const { idToken } = JSON.parse(atob(base64Data));
      setToken(idToken);
    } catch (error) {
      Logger.error(`Failed to parse auth data from URL parameter: ${error}`);
      navigate(FALLBACK_PATH);
    }
  }, [base64Data, navigate]);

  // Verify auth token and navigate accordingly
  useEffect(() => {
    if (!verificationInProgress) return;

    const authProvider = getStoredAuthProvider();
    if (!authProvider) {
      navigate(FALLBACK_PATH);
      return;
    }
    if (!token) return;

    const verifyAuth = async () => {
      const signupMethod = SIGNUP_METHODS[authProvider];
      const userInfo = await handleVerifyAuth(token, authProvider);
      if (!userInfo) {
        sendFailedSignupEvent({
          signup_method: signupMethod,
          signup_option:
            authFlow === AuthFlow.LOGIN
              ? SignupOptionProperty.LOG_IN
              : SignupOptionProperty.SIGN_UP,
        });
        navigate(FALLBACK_PATH);
        return;
      }

      setUser(userInfo);
      const isReturningUser = userInfo.isReturning;
      const { id: userid, email, username } = userInfo.user;
      let redirectPath;

      const phxClickId = getPhoenixClickId();
      if (phxClickId) {
        savePhoenixClick(phxClickId);
      }

      if (isReturningUser) {
        sendSuccessfulSigninEvent(signupMethod, { userid, email, username });
        redirectPath = ROUTES.ROOT.DASHBOARD;
      } else {
        const attribution = getAttributionData();
        if (attribution) {
          saveAttribution(attribution);
        }

        sendSuccessfulSignupEvent(signupMethod, { userid, email, username });
        goToStep(OnboardingSteps.CLAIM_USERNAME);
        redirectPath = ROUTES.ROOT.SIGN_UP;
      }

      navigate(redirectPath);
    };
    verifyAuth();
  }, [
    token,
    authFlow,
    goToStep,
    navigate,
    setUser,
    sendFailedSignupEvent,
    sendSuccessfulSignupEvent,
    sendSuccessfulSigninEvent,
    verificationInProgress,
    saveAttribution,
    savePhoenixClick,
  ]);

  return {
    authFlowText,
    verificationInProgress,
  };
}
